import { Copy, ExternalLink, PenTool } from 'lucide-react'
import { useState } from 'react'
import { SiShopify } from 'react-icons/si'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

interface ShopifyArticle {
  id: string
  title: string
  handle: string
  author?: string | null
  published: boolean
  publishedAt?: string | null
  url: string
}

interface ShopifyBlogPostsListProps {
  articles: ShopifyArticle[]
  blogHandle?: string
  shopDomain?: string
  hasNextPage?: boolean
  showCount?: boolean
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

const getPublishedStatusColor = (published: boolean) => {
  return published
    ? 'bg-green-100 text-green-700 border-green-200'
    : 'bg-yellow-100 text-yellow-700 border-yellow-200'
}

const getPublishedIcon = (published: boolean) => {
  return published ? '🟢' : '🟡'
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return null
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateString
  }
}

export function ShopifyBlogPostsList({
  articles,
  blogHandle,
  shopDomain,
  hasNextPage = false,
  showCount = true,
}: ShopifyBlogPostsListProps) {
  const [showDetails, setShowDetails] = useState(false)

  if (articles.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
        <PenTool className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-600">
          No blog posts found{blogHandle && ` in "${blogHandle}" blog`}
        </span>
      </div>
    )
  }

  const contextActions: ContextAction[] = [
    {
      id: 'copy-all-ids',
      label: 'Copy All Article IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(articles.map((a) => a.id).join('\n'))
      },
    },
    {
      id: 'copy-all-handles',
      label: 'Copy All Article Handles',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(articles.map((a) => a.handle).join('\n'))
      },
    },
    {
      id: 'copy-all-urls',
      label: 'Copy All Article URLs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(articles.map((a) => a.url).join('\n'))
      },
    },
    {
      id: 'copy-published-urls',
      label: 'Copy Published Article URLs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        const publishedUrls = articles.filter((a) => a.published).map((a) => a.url)
        await copyToClipboard(publishedUrls.join('\n'))
      },
    },
    ...(shopDomain && blogHandle
      ? [
          {
            id: 'open-blog-admin',
            label: 'Open Blog in Shopify Admin',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => {
              window.open(`https://${shopDomain}/admin/blogs`, '_blank', 'noopener,noreferrer')
            },
          },
        ]
      : []),
  ]

  const publishedCount = articles.filter((a) => a.published).length
  const draftCount = articles.length - publishedCount

  // Group articles by author if there are multiple authors
  const authorGroups = articles.reduce(
    (groups, article) => {
      const author = article.author || 'Unknown Author'
      if (!groups[author]) {
        groups[author] = []
      }
      groups[author].push(article)
      return groups
    },
    {} as Record<string, ShopifyArticle[]>
  )

  const hasMultipleAuthors = Object.keys(authorGroups).length > 1

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="space-y-3">
        {showCount && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <SiShopify className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">
                  {articles.length} Blog Post{articles.length === 1 ? '' : 's'}
                  {blogHandle && ` from "${blogHandle}"`}
                  {hasNextPage && ' (more available)'}
                </span>
              </div>
              {articles.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="flex items-center gap-1">🟢 {publishedCount} published</span>
                  {draftCount > 0 && (
                    <span className="flex items-center gap-1">🟡 {draftCount} draft</span>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
              <span className={`transform transition-transform ${showDetails ? 'rotate-90' : ''}`}>
                ▶
              </span>
            </button>
          </div>
        )}

        <div className="space-y-4">
          {hasMultipleAuthors && showDetails ? (
            // Group by author when there are multiple authors and details are shown
            Object.entries(authorGroups).map(([author, authorArticles]) => (
              <div key={author} className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <PenTool className="h-4 w-4" />
                  <span>{author}</span>
                  <span className="text-gray-500">({authorArticles.length})</span>
                </div>
                <div className="ml-6 grid gap-2">
                  {authorArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} showDetails={showDetails} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Simple list when there's one author or details are hidden
            <div className="grid gap-2">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} showDetails={showDetails} />
              ))}
            </div>
          )}
        </div>

        {hasNextPage && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <span>📄</span>
              <span>More blog posts available. Use pagination to fetch additional results.</span>
            </div>
          </div>
        )}
      </div>
    </ContextMenuWrapper>
  )
}

interface ArticleCardProps {
  article: ShopifyArticle
  showDetails: boolean
}

function ArticleCard({ article, showDetails }: ArticleCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <h4 className="truncate font-medium text-gray-900">{article.title}</h4>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getPublishedStatusColor(article.published)}`}
            >
              <span className="mr-1">{getPublishedIcon(article.published)}</span>
              {article.published ? 'Published' : 'Draft'}
            </span>
          </div>

          <div className="space-y-0.5 text-xs text-gray-500">
            <div>
              Handle: <span className="font-mono">{article.handle}</span>
            </div>
            {article.author && (
              <div>
                Author: <span className="text-gray-700">{article.author}</span>
              </div>
            )}
            {article.published && article.publishedAt && (
              <div>
                Published: <span className="text-gray-700">{formatDate(article.publishedAt)}</span>
              </div>
            )}
            {showDetails && (
              <div>
                ID: <span className="font-mono">{article.id}</span>
              </div>
            )}
          </div>
        </div>

        <div className="ml-3 flex items-center gap-1">
          <button
            onClick={() => copyToClipboard(article.handle)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Copy handle"
          >
            <Copy className="h-3 w-3" />
          </button>
          {article.url && (
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
              title="View article"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
